/**
 * @file src/views/VID/helpers/trackVidIdCapture.ts
 * @exports createCaptureFlowId, trackVidCameraResume, trackVidIdCapture, trackVidIdCaptureOnPageExit, VidIdCapturePath, VidIdCaptureReason
 * @summary Defines and sends the shared Mixpanel event contracts for the vID ID capture funnel and camera resume checks.
 * @remarks Capture events carry a summary of camera returns so a restart or terminal outcome can be tied to the last
 *   return. Camera resume events report camera-resume failures, not confirmed white screens. A `camera_unavailable`
 *   failure carries the bounded camera failure reason shared with the App.
 */

import { analytics, ANALYTICS, type AnalyticsCameraFailReason, type AnalyticsCameraResumeResult } from "src/helpers";
import { createNanoID } from "src/helpers/utils/randomIds";
import type { CameraResumeObservation } from "../hooks/autoCapture/CameraResumeMonitor";

const VID_ID_CAPTURE_EVENT = ANALYTICS.idCaptureProgressed;

export type VidIdCapturePath = "local" | "remote";
type VidIdCaptureStage = "auto_capture" | "document_scan" | "remote_sync";
type VidIdCaptureOutcome = "abandoned" | "failure" | "fallback" | "reloaded_after_exit" | "started" | "success";
export type VidIdCaptureReason =
	| "auto_capture_timeout"
	| "capture_closed"
	| "camera_unavailable"
	| "connection_failed"
	| "file_creation_failed"
	| "manual_capture_selected"
	| "page_navigation"
	| "scan_rejected"
	| "selected_file_unavailable"
	| "session_expired"
	| "unknown";

type VidIdCaptureProperties = {
	barcodeRequired?: boolean;
	cameraFacingMode?: string;
	cameraFailReason?: AnalyticsCameraFailReason;
	cameraFrameRate?: number;
	cameraHeight?: number;
	cameraReturnCount?: number;
	cameraWidth?: number;
	captureCompleted?: boolean;
	captureFlowId: string;
	captureHeight?: number;
	captureSessionId?: string;
	captureWidth?: number;
	clientFramesSubmitted?: number;
	durationMs?: number;
	jobId?: string;
	landedScreen?: string;
	lastCameraReturnResult?: AnalyticsCameraResumeResult;
	lastCompletedStage?: string;
	msSinceExit?: number;
	msSinceLastCameraReturn?: number;
	networkDegradedObserved?: boolean;
	outcome: VidIdCaptureOutcome;
	pageHiddenAtFailure?: boolean;
	path: VidIdCapturePath;
	reason?: VidIdCaptureReason;
	selectedFrameFileCreated?: boolean;
	selectionReceived?: boolean;
	side?: "back" | "front";
	stage: VidIdCaptureStage;
	webrtcConnectionState?: string;
	webrtcEncodedFps?: number;
	webrtcEncodedHeight?: number;
	webrtcEncodedWidth?: number;
	webrtcFramesEncoded?: number;
	webrtcFramesSent?: number;
	webrtcQualityLimitationReason?: string;
	webrtcRoundTripTimeMs?: number;
	webrtcSendBitrateKbps?: number;
};

type VidCameraResumeProperties = CameraResumeObservation & {
	captureFlowId: string;
	path: VidIdCapturePath;
	side: "back" | "front";
};

interface TrackVidCameraResumeArgs {
	properties: VidCameraResumeProperties;
	usePageExitTransport: boolean;
}

const withoutEmptyValues = (properties: object) =>
	Object.fromEntries(Object.entries(properties).filter(([, value]) => value !== undefined && value !== null));

export const createCaptureFlowId = () => `capture-flow-${createNanoID(16)}`;

export const trackVidIdCapture = (properties: VidIdCaptureProperties) => {
	analytics.track(VID_ID_CAPTURE_EVENT, withoutEmptyValues(properties));
};

export const trackVidIdCaptureOnPageExit = (properties: VidIdCaptureProperties) => {
	analytics.trackOnPageExit({ eventData: withoutEmptyValues(properties), eventName: VID_ID_CAPTURE_EVENT });
};

export const trackVidCameraResume = ({ properties, usePageExitTransport }: TrackVidCameraResumeArgs) => {
	const eventData = withoutEmptyValues(properties);
	if (usePageExitTransport) {
		analytics.trackOnPageExit({ eventData, eventName: ANALYTICS.idCameraResumeChecked });
		return;
	}
	analytics.track(ANALYTICS.idCameraResumeChecked, eventData);
};
